import React, { useState } from 'react';
import { useDatabase } from '../../../db/DatabaseProvider';
import { useAuthStore } from '../../../stores';
import { useToast } from '../../common/ToastProvider';
import { Button } from '../../common/Button';
import type { CustomerDocType } from '../../../db/schemas';
import { Edit2, Save, X, FileText } from 'lucide-react';
import { now } from '../../../lib/uuid';

interface Props {
  customer?: CustomerDocType | null;
}

export function CustomerTabNotes({ customer }: Props) {
  const targetCustomer = customer;
  if (!targetCustomer) return null;

  const db = useDatabase();
  const user = useAuthStore(s => s.user);
  const toast = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotas] = useState(customer?.notes || '');

  const handleSave = async () => {
    if (!db || isSubmitting || !customer?.id) return;
    setIsSubmitting(true);
    try {
      const doc = await db.customers.findOne(customer?.id).exec();
      if (doc) {
        await doc.patch({
          notes: notes || null,
          updated_at: now(),
          last_modified_by: user?.email || 'system',
          
        });
        toast.success('Notas guardadas correctamente');
        setIsEditing(false);
      }
    } catch (e: any) {
      console.error(e);
      toast.error('Error al guardar notes: ' + (e.message || 'Error desconocido'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNotas(customer.notes || '');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-text-primary">Notas Clínicas</h3>
        {!isEditing ? (
          <Button variant="ghost" size="sm" icon={Edit2} onClick={() => setIsEditing(true)}>Editar Notas</Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" icon={X} onClick={handleCancel}>Cancelar</Button>
            <Button variant="primary" size="sm" icon={Save} onClick={handleSave} isLoading={isSubmitting}>Guardar</Button>
          </div>
        )}
      </div>

      <div className="bg-surface border border-border rounded-xl p-5 shadow-xs">
        {isEditing ? (
          <textarea
            rows={10}
            value={notes}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Escribe aquí notes sobre preferencias del cliente, type de piel, contraindicaciones..."
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none resize-y"
          />
        ) : (
          <div>
            {customer.notes ? (
              <p className="whitespace-pre-line text-sm text-text-primary leading-relaxed">{customer.notes}</p>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
                <p className="text-sm text-text-secondary">No hay notes registradas para este cliente.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
